/**
 * DirectorThought
 *
 * A premium text component for Director "thinking" states.
 * Features:
 * - "Thinking" text with slate → white gradient
 * - Subtle shimmer animation moving through the gradient
 * - Independently animated trailing ellipsis
 */

import { memo } from 'react'
import { cn } from '../../lib/utils'

interface DirectorThoughtProps {
  className?: string
}

export const DirectorThought = memo(({
  className,
}: DirectorThoughtProps) => {
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      {/* Slate spinner */}
      <span className="director-spinner" />

      {/* "Thinking" with gradient shimmer */}
      <span className="director-thought-text">
        Thinking
      </span>

      {/* Animated ellipsis */}
      <span className="director-thought-ellipsis">
        <span className="dot dot-1">.</span>
        <span className="dot dot-2">.</span>
        <span className="dot dot-3">.</span>
      </span>

      {/* Inline styles for the animations */}
      <style>{`
        .director-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(100, 116, 139, 0.2);
          border-top-color: rgba(100, 116, 139, 0.9);
          border-radius: 50%;
          animation: director-spin 0.8s linear infinite;
        }

        @keyframes director-spin {
          to {
            transform: rotate(360deg);
          }
        }

        .director-thought-text {
          background: linear-gradient(
            90deg,
            rgba(100, 116, 139, 0.9) 0%,
            rgba(255, 255, 255, 0.95) 50%,
            rgba(100, 116, 139, 0.9) 100%
          );
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: director-shimmer 3s ease-in-out infinite;
        }

        @keyframes director-shimmer {
          0%, 100% {
            background-position: 200% center;
          }
          50% {
            background-position: 0% center;
          }
        }

        .director-thought-ellipsis {
          display: inline-flex;
          margin-left: 0;
        }

        .director-thought-ellipsis .dot {
          background: linear-gradient(
            90deg,
            rgba(100, 116, 139, 0.9) 0%,
            rgba(255, 255, 255, 0.95) 50%,
            rgba(100, 116, 139, 0.9) 100%
          );
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: director-shimmer 3s ease-in-out infinite, director-dot-pulse 1.4s ease-in-out infinite;
        }

        .director-thought-ellipsis .dot-1 {
          animation-delay: 0s, 0s;
        }

        .director-thought-ellipsis .dot-2 {
          animation-delay: 0s, 0.2s;
        }

        .director-thought-ellipsis .dot-3 {
          animation-delay: 0s, 0.4s;
        }

        @keyframes director-dot-pulse {
          0%, 60%, 100% {
            opacity: 0.6;
            transform: translateY(0);
          }
          30% {
            opacity: 1;
            transform: translateY(-1px);
          }
        }
      `}</style>
    </span>
  )
})

DirectorThought.displayName = 'DirectorThought'

export default DirectorThought
